import {
  Camera,
  ColorCurves,
  Constants,
  EngineStore,
  FxaaPostProcess,
  type IDisposable,
  ImageProcessingConfiguration,
  ImageProcessingPostProcess,
  Logger,
  type Nullable,
  Observable,
  Observer,
  PostProcess,
  PostProcessRenderEffect,
  PostProcessRenderPipeline,
  Scene,
  Texture,
} from '@babylonjs/core';

import { RenderManager } from '../../manager';

import { BloomEffect } from './BloomEffect';
import {
  AntialiasingOptions,
  BloomOptions,
  defaultPostprocessOptions,
  PostprocessOptions,
  PostprocessParameters,
  SSAOOptions,
} from './PostProcess';
import { SSAOEffect } from './SSAOEffect';

export class ZEDRenderingPipeline extends PostProcessRenderPipeline implements IDisposable {
  public override getClassName(): string {
    return 'ZEDRenderingPipeline';
  }

  // ---------------------------------
  private scene!: Scene;

  private camerasToBeAttached: Array<Camera> = [];
  private hdr!: boolean;
  private defaultPipelineTextureType!: number;

  public onBuildObservable = new Observable<ZEDRenderingPipeline>();
  private imageProcessingConfigurationObserver: Nullable<Observer<ImageProcessingConfiguration>> = null;

  // ---------------------------------
  private fxaa!: FxaaPostProcess;
  private imageProcessing!: ImageProcessingPostProcess;
  private ssao!: SSAOEffect;
  private bloom!: BloomEffect;

  // ---------------------------------
  private options: PostprocessOptions = defaultPostprocessOptions;
  public getOptions(): PostprocessOptions {
    return JSON.parse(JSON.stringify(this.options));
  }
  private cacheOptions?: PostprocessOptions | undefined;
  private _enabled: boolean = true;
  public get enabled() {
    return this._enabled;
  }
  public set enabled(enabled: boolean) {
    this._enabled = enabled;
    if (this._enabled) {
      if (this.cacheOptions) {
        this.updateOptions({ ...this.cacheOptions });
        this.cacheOptions = undefined;
      }
    } else {
      this.cacheOptions = { ...this.options };
      this.updateOptions(
        {
          imageProcessing: {
            enabled: false,
          },
          colorCurve: {
            enabled: false,
          },
          toneMapping: {
            enabled: false,
          },
          vignette: {
            enabled: false,
          },
          bloom: {
            enabled: false,
          },
          ssao: {
            enabled: false,
          },
        },
        true
      );
    }
  }

  public updateOptions(params: PostprocessParameters, forceUpdate: boolean = false) {
    const requireBuild = <T>(prop: keyof PostprocessOptions, key: keyof T) =>
      params !== undefined &&
      params[prop] !== undefined &&
      //@ts-ignore
      params[prop][key] !== undefined &&
      //@ts-ignore
      params[prop][key] !== this.options[prop][key];

    let buildBloom = false;
    let buildPipeline = false;

    buildBloom = requireBuild<BloomOptions>('bloom', 'scale');

    buildPipeline =
      (params.enabled !== undefined && this.options.enabled !== params.enabled) ||
      requireBuild<AntialiasingOptions>('antialiasing', 'fxaaEnabled') ||
      requireBuild<AntialiasingOptions>('antialiasing', 'msaaEnabled') ||
      requireBuild<BloomOptions>('bloom', 'enabled') ||
      buildBloom ||
      requireBuild<SSAOOptions>('ssao', 'enabled');

    this.setOptions(params, forceUpdate);

    if (buildBloom) {
      this.rebuildBloom();
    }

    if (buildPipeline) {
      this.buildPipeline();
    }

    this.renderManager.renderUpdate();
  }
  private setOptions(params: PostprocessParameters, forceUpdate: boolean = false) {
    if (params.enabled !== undefined) this.options.enabled = params.enabled;

    if ((forceUpdate && this.options.antialiasing) || params.antialiasing) {
      this.options.antialiasing = { ...this.options.antialiasing, ...params.antialiasing };
    }
    if ((forceUpdate && this.options.imageProcessing) || params.imageProcessing) {
      this.options.imageProcessing = { ...this.options.imageProcessing, ...params.imageProcessing };
      this.updateImageProcessing();
    }
    if ((forceUpdate && this.options.antialiasing) || params.toneMapping) {
      this.options.toneMapping = { ...this.options.toneMapping, ...params.toneMapping };
      this.updateToneMapping();
    }
    if ((forceUpdate && this.options.colorCurve) || params.colorCurve) {
      this.options.colorCurve = { ...this.options.colorCurve, ...params.colorCurve };
      this.updateColorCurve();
    }
    if ((forceUpdate && this.options.vignette) || params.vignette) {
      this.options.vignette = { ...this.options.vignette, ...params.vignette };
      this.updateVignette();
    }
    if ((forceUpdate && this.options.bloom) || params.bloom) {
      this.options.bloom = { ...this.options.bloom, ...params.bloom };
      this.bloom?.updateOptions(this.options.bloom);
    }
    if ((forceUpdate && this.options.ssao) || params.ssao) {
      this.options.ssao = { ...this.options.ssao, ...params.ssao };
      this.ssao?.updateOptions(this.options.ssao);
    }
  }

  private updateImageProcessing() {
    const enable = this.options.imageProcessing.enabled;
    const config = this.scene.imageProcessingConfiguration;
    config.contrast = enable ? this.options.imageProcessing.contrast : 1;
    config.exposure = enable ? this.options.imageProcessing.exposure : 1;
  }
  private updateToneMapping() {
    const config = this.scene.imageProcessingConfiguration;
    config.toneMappingEnabled = this.options.toneMapping.enabled;
    config.toneMappingType = this.options.toneMapping.type;
  }
  private updateColorCurve() {
    const cc = this.options.colorCurve;
    this.imageProcessing.colorCurvesEnabled = cc.enabled;
    if (cc.enabled) {
      const curve = this.imageProcessing.colorCurves ?? new ColorCurves();
      curve.globalDensity = cc.globalDensity;
      curve.globalExposure = cc.globalExposure;
      curve.globalHue = cc.globalHue;
      curve.globalSaturation = cc.globalSaturation;

      curve.highlightsDensity = cc.highlightsDensity;
      curve.highlightsExposure = cc.highlightsExposure;
      curve.highlightsHue = cc.highlightsHue;
      curve.highlightsSaturation = cc.highlightsSaturation;

      curve.midtonesDensity = cc.midtonesDensity;
      curve.midtonesExposure = cc.midtonesExposure;
      curve.midtonesHue = cc.midtonesHue;
      curve.midtonesSaturation = cc.midtonesSaturation;

      curve.shadowsDensity = cc.shadowsDensity;
      curve.shadowsExposure = cc.shadowsExposure;
      curve.shadowsHue = cc.shadowsHue;
      curve.shadowsSaturation = cc.shadowsSaturation;

      this.imageProcessing.colorCurves = curve;
    }
  }
  private updateVignette() {
    const config = this.scene.imageProcessingConfiguration;
    if (!config.isEnabled) config.isEnabled = true;
    config.vignetteEnabled = this.options.vignette.enabled;
    config.vignetteWeight = this.options.vignette.vignetteWeight;
    config.vignetteCameraFov = this.options.vignette.vignetteCameraFov;
  }

  private rebuildBloom() {
    if (!this.bloom) return;

    // recreate bloom and dispose old as this setting is not dynamic
    const oldBloom = this.bloom;
    this.bloom = new BloomEffect(this.scene, this.options.bloom, this.defaultPipelineTextureType, false);
    this.bloom.updateOptions({
      threshold: oldBloom.options.threshold,
    });

    for (let i = 0; i < this._cameras.length; i++) {
      if (this._cameras[i] !== undefined) {
        const camera = this._cameras[i];
        if (camera) {
          oldBloom.disposeEffects(camera);
        }
      }
    }
  }

  constructor(
    name = '',
    options: Partial<PostprocessParameters> | undefined,
    scene: Scene = EngineStore.LastCreatedScene!,
    private readonly renderManager: RenderManager,
    cameras?: Camera[]
  ) {
    super(scene.getEngine(), name);
    this.initailize(options, scene, cameras);

    this.scene.onActiveCameraChanged.add((scene) => {
      if (scene.activeCamera) {
        this.dispose();
        this.initailize(this.options, scene, [scene.activeCamera]);
      }
    });
  }

  private initailize(
    options: Partial<PostprocessParameters> | undefined,
    scene: Scene = EngineStore.LastCreatedScene!,
    cameras?: Camera[]
  ) {
    scene.postProcessesEnabled = true;
    this._cameras = cameras || scene.cameras;
    this._cameras = this._cameras.slice();
    this.camerasToBeAttached = this._cameras.slice();

    // Initialize
    this.scene = scene;
    if (this.scene.prePassRenderer) {
      this.scene.prePassRenderer!.disableGammaTransform = true;
    }
    const caps = this.scene.getEngine().getCaps();

    // hdr 강제 설정
    const hdr = true;
    this.hdr = hdr && (caps.textureHalfFloatRender || caps.textureFloatRender);

    // Misc
    if (this.hdr) {
      if (caps.textureHalfFloatRender) {
        this.defaultPipelineTextureType = Constants.TEXTURETYPE_HALF_FLOAT;
      } else if (caps.textureFloatRender) {
        this.defaultPipelineTextureType = Constants.TEXTURETYPE_FLOAT;
      }
    } else {
      this.defaultPipelineTextureType = Constants.TEXTURETYPE_UNSIGNED_INT;
    }

    // Attach
    scene.postProcessRenderPipelineManager.addPipeline(this);

    this.imageProcessing = new ImageProcessingPostProcess(
      'imageProcessing',
      1.0,
      null,
      Texture.BILINEAR_SAMPLINGMODE,
      scene.getEngine(),
      false,
      this.defaultPipelineTextureType,
      this.scene.imageProcessingConfiguration
    );

    if (this.options.bloom.enabled)
      this.bloom = new BloomEffect(this.scene, this.options.bloom, this.defaultPipelineTextureType, true);

    if (this.options.ssao.enabled)
      this.ssao = new SSAOEffect(this.scene, this.options.ssao, this.defaultPipelineTextureType, true);

    if (this.options.antialiasing.fxaaEnabled)
      this.fxaa = new FxaaPostProcess(
        'fxaa',
        1.0,
        null,
        Texture.BILINEAR_SAMPLINGMODE,
        scene.getEngine(),
        false,
        this.defaultPipelineTextureType
      );

    this.imageProcessingConfigurationObserver = this.scene.imageProcessingConfiguration.onUpdateParameters.add(() => {
      if (this.bloom) this.bloom.downscale._exposure = this.scene.imageProcessingConfiguration.exposure;
    });

    this.buildPipeline();
    if (options) this.updateOptions(options, true);
    else this.setOptions({}, true);
  }
  private _hasCleared = false;
  private _prevPostProcess: Nullable<PostProcess> = null;
  private _prevPrevPostProcess: Nullable<PostProcess> = null;

  private _setAutoClearAndTextureSharing(postProcess: PostProcess, skipTextureSharing = false) {
    if (this._hasCleared) {
      postProcess.autoClear = false;
    } else {
      postProcess.autoClear = true;
      this.scene.autoClear = false;
      this._hasCleared = true;
    }

    if (!skipTextureSharing) {
      if (this._prevPrevPostProcess) {
        postProcess.shareOutputWith(this._prevPrevPostProcess);
      } else {
        postProcess.useOwnOutput();
      }

      if (this._prevPostProcess) {
        this._prevPrevPostProcess = this._prevPostProcess;
      }
      this._prevPostProcess = postProcess;
    }
  }

  private _activeCameraChangedObserver: Nullable<Observer<Scene>> = null;
  private _activeCamerasChangedObserver: Nullable<Observer<Scene>> = null;

  // ---------------------------------
  // Core
  // ---------------------------------
  private buildPipeline() {
    // console.log('buildPipeline');
    this.scene.autoClear = true;

    const engine = this.scene.getEngine();
    this.disposePostProcesses();
    if (this._cameras !== null) {
      this.scene.postProcessRenderPipelineManager.detachCamerasFromRenderPipeline(this._name, this._cameras);
      // get back cameras to be used to reattach pipeline
      this._cameras = this.camerasToBeAttached.slice();
    }
    this._reset();
    this._prevPostProcess = null;
    this._prevPrevPostProcess = null;
    this._hasCleared = false;

    if (this.options.enabled) {
      if (this.options.ssao.enabled) {
        if (!this.ssao) {
          this.ssao = new SSAOEffect(this.scene, this.options.ssao, this.defaultPipelineTextureType, true);
        }

        if (!this.ssao.isReady()) {
          this.ssao.updateEffects();
        }
        this.addEffect(this.ssao);
        if (this.ssao.effects[0]) {
          this._setAutoClearAndTextureSharing(this.ssao.effects[0], true);
        }
      }

      if (this.options.bloom.enabled) {
        if (!this.bloom) {
          this.bloom = new BloomEffect(this.scene, this.options.bloom, this.defaultPipelineTextureType, true);
        }

        if (!this.bloom.isReady()) {
          this.bloom.updateEffects();
        }
        this.addEffect(this.bloom);
        if (this.bloom.effects[0]) {
          this._setAutoClearAndTextureSharing(this.bloom.effects[0], true);
        }
      }

      if (this.hdr) {
        this.addEffect(
          new PostProcessRenderEffect(
            engine,
            'ImageProcessingPostProcessEffect',
            () => {
              return this.imageProcessing;
            },
            true
          )
        );
        this._setAutoClearAndTextureSharing(this.imageProcessing);
      } else {
        this.scene.imageProcessingConfiguration.applyByPostProcess = false;
      }

      if (!this._cameras || this._cameras.length === 0) {
        this.scene.imageProcessingConfiguration.applyByPostProcess = false;
      }

      if (!this.imageProcessing.getEffect()) {
        this.imageProcessing._updateParameters();
      }

      if (this.options.antialiasing.fxaaEnabled) {
        if (!this.fxaa) {
          this.fxaa = new FxaaPostProcess(
            'fxaa',
            1.0,
            null,
            Texture.BILINEAR_SAMPLINGMODE,
            this.scene.getEngine(),
            false,
            this.defaultPipelineTextureType
          );
        }
        this.addEffect(
          new PostProcessRenderEffect(
            engine,
            'FxaaPostProcessEffect',
            () => {
              return this.fxaa;
            },
            true
          )
        );
        this._setAutoClearAndTextureSharing(this.fxaa, true);
      }
    }

    if (this._cameras !== null) {
      this.scene.postProcessRenderPipelineManager.attachCamerasToRenderPipeline(this._name, this._cameras);
    }

    // In multicamera mode, the scene needs to autoclear in between cameras.
    if (
      (this.scene.activeCameras && this.scene.activeCameras.length > 1) ||
      (this.scene.activeCamera && this._cameras.indexOf(this.scene.activeCamera) === -1)
    ) {
      this.scene.autoClear = true;
    }
    // The active camera on the scene can be changed anytime
    if (!this._activeCameraChangedObserver) {
      this._activeCameraChangedObserver = this.scene.onActiveCameraChanged.add(() => {
        if (this.scene.activeCamera && this._cameras.indexOf(this.scene.activeCamera) === -1) {
          this.scene.autoClear = true;
        }
      });
    }
    if (!this._activeCamerasChangedObserver) {
      this._activeCamerasChangedObserver = this.scene.onActiveCamerasChanged.add(() => {
        if (this.scene.activeCameras && this.scene.activeCameras.length > 1) {
          this.scene.autoClear = true;
        }
      });
    }

    if (this.options.enabled) {
      const samples = this.options.antialiasing.msaaEnabled ? 4 : 1;
      if (this.ssao && this.options.ssao.enabled) {
        this._enableMSAAOnFirstPostProcess(1);
        this.ssao.textureSamples = samples;
      } else {
        if (!this._enableMSAAOnFirstPostProcess(samples) && samples > 1) {
          Logger.Warn('MSAA failed to enable, MSAA is only supported in browsers that support webGL >= 2.0');
        }
      }
    }
    this.renderManager.renderUpdate();
    this.onBuildObservable.notifyObservers(this);
  }

  // ---------------------------------
  // Dispose
  // ---------------------------------
  private disposePostProcesses(disposeNonRecreated = false): void {
    for (let i = 0; i < this._cameras.length; i++) {
      const camera = this._cameras[i];
      if (!camera) continue;

      // These are created in the constructor and should not be disposed on every pipeline change
      if (disposeNonRecreated) {
        this.imageProcessing?.dispose(camera);
        this.fxaa?.dispose(camera);
        this.ssao?.disposeEffects(camera);
        this.bloom?.disposeEffects(camera);
      }
    }

    if (disposeNonRecreated) {
      this.bloom?.dispose();
      this.ssao?.dispose();
      (<any>this.imageProcessing) = null;
      (<any>this.fxaa) = null;
      (<any>this.ssao) = null;
      (<any>this.bloom) = null;
    }
  }
  public override dispose(): void {
    this.onBuildObservable.clear();
    this.disposePostProcesses(true);
    this.scene.postProcessRenderPipelineManager.detachCamerasFromRenderPipeline(this._name, this._cameras);
    this.scene._postProcessRenderPipelineManager.removePipeline(this.name);
    this.scene.autoClear = true;
    this.scene.onActiveCameraChanged.remove(this._activeCameraChangedObserver);
    this.scene.onActiveCamerasChanged.remove(this._activeCamerasChangedObserver);

    this.scene.imageProcessingConfiguration.onUpdateParameters.remove(this.imageProcessingConfigurationObserver);
    super.dispose();
  }

  // ---------------------------------
  // Camera
  // ---------------------------------
  public addCamera(camera: Camera): void {
    this.camerasToBeAttached.push(camera);
    this.buildPipeline();
  }
  public removeCamera(camera: Camera): void {
    const index = this.camerasToBeAttached.indexOf(camera);
    this.camerasToBeAttached.splice(index, 1);
    this.buildPipeline();
  }
}
